import { type ActivatedRouteSnapshot, type Data } from '@angular/router';
import { snapshot } from '@principle-theorem/shared';
import {
  type IManagementResource,
  type IRelatedResource,
  type ManagementResource,
} from './models/management-resource';
import { type IResourceContext } from './models/management-tool';

export type ContextFactory = (
  resource: IManagementResource,
  value: unknown,
  routeDataKey: string
) => IResourceContext;

interface IMatchedContext {
  context: IResourceContext;
  initialValue: unknown;
}

export class ResourceContextBuilder {
  static fromRoute(
    root: ActivatedRouteSnapshot,
    resources: IManagementResource[],
    buildContext: ContextFactory
  ): IResourceContext[] {
    const allMatches = this._flattenRouteData(root).reduce<IMatchedContext[]>(
      (acc, data) => [
        ...acc,
        ...this._findMatchingContexts(data, resources, buildContext, acc),
      ],
      []
    );
    return allMatches.map((match) => match.context);
  }

  /**
   * Appends related resources (resolved async) to the given route contexts, deduplicating by type.
   */
  static async resolveRelated(
    routeContexts: IResourceContext[],
    resources: IManagementResource[],
    buildContext: ContextFactory
  ): Promise<IResourceContext[]> {
    const relatedContexts = await this._resolveRelatedContexts(
      routeContexts,
      resources,
      buildContext
    );
    return [...routeContexts, ...relatedContexts];
  }

  private static _filterNewRelatedContexts(
    relatedResources: IRelatedResource<unknown>[],
    resources: IManagementResource[],
    buildContext: ContextFactory,
    routeContexts: IResourceContext[],
    existingRelated: IResourceContext[]
  ): IResourceContext[] {
    return relatedResources.reduce<IResourceContext[]>((acc, related) => {
      if (this._hasResourceType(routeContexts, related.resource)) {
        return acc;
      }
      if (
        this._hasResourceType([...existingRelated, ...acc], related.resource)
      ) {
        return acc;
      }
      const context = this._buildRelatedContext(
        related,
        resources,
        buildContext
      );
      return context ? [...acc, context] : acc;
    }, []);
  }

  private static _flattenRouteData(route: ActivatedRouteSnapshot): Data[] {
    return [
      route.data,
      ...route.children.flatMap((child) => this._flattenRouteData(child)),
    ];
  }

  private static _findMatchingContexts(
    data: Data,
    resources: IManagementResource[],
    buildContext: ContextFactory,
    existing: IMatchedContext[]
  ): IMatchedContext[] {
    const definedEntries = Object.entries(data).filter(
      ([_, value]) => value !== undefined
    );

    return definedEntries.reduce<IMatchedContext[]>(
      (results, [key, value]) => [
        ...results,
        ...this._matchResourcesForEntry(
          resources,
          key,
          value,
          [...existing, ...results],
          buildContext
        ),
      ],
      []
    );
  }

  private static _matchResourcesForEntry(
    resources: IManagementResource[],
    key: string,
    value: unknown,
    existing: IMatchedContext[],
    buildContext: ContextFactory
  ): IMatchedContext[] {
    return resources.reduce<IMatchedContext[]>((matches, resource) => {
      const match = this._matchResourceToEntry(
        resource,
        key,
        value,
        [...existing, ...matches],
        buildContext
      );
      return match ? [...matches, match] : matches;
    }, []);
  }

  private static _matchResourceToEntry(
    resource: IManagementResource,
    key: string,
    value: unknown,
    existing: IMatchedContext[],
    buildContext: ContextFactory
  ): IMatchedContext | undefined {
    if (!resource.typeGuard(value)) {
      return undefined;
    }
    const isDuplicate = existing.some((match) =>
      this._isSameResource(
        resource,
        key,
        value,
        match.context,
        match.initialValue
      )
    );
    if (isDuplicate) {
      return undefined;
    }
    const context = buildContext(resource, value, key);
    return { context, initialValue: value };
  }

  private static _isSameResource<T = unknown>(
    resource: IManagementResource<T>,
    resourceKey: string,
    resourceValue: T,
    context: IResourceContext<T>,
    contextValue: T
  ): boolean {
    if (context.resource.resource !== resource.resource) {
      return false;
    }
    if (!resource.uniqueBy) {
      return resourceKey === context.routeDataKey;
    }
    return resource.uniqueBy(resourceValue) === resource.uniqueBy(contextValue);
  }

  /**
   * Creates a context for a related resource by finding its matching resource definition.
   */
  private static _buildRelatedContext(
    related: IRelatedResource<unknown>,
    resources: IManagementResource[],
    buildContext: ContextFactory
  ): IResourceContext | undefined {
    const matchingResource = resources.find(
      (resource) => resource.resource === related.resource
    );
    if (!matchingResource) {
      return undefined;
    }
    return buildContext(
      matchingResource,
      related.value,
      `_related:${related.resource}`
    );
  }

  private static _hasResourceType(
    contexts: IResourceContext[],
    resourceType: ManagementResource
  ): boolean {
    return contexts.some((ctx) => ctx.resource.resource === resourceType);
  }

  private static async _resolveRelatedContexts(
    routeContexts: IResourceContext[],
    resources: IManagementResource[],
    buildContext: ContextFactory
  ): Promise<IResourceContext[]> {
    const relatedContexts: IResourceContext[] = [];

    for (const context of routeContexts) {
      if (!context.resource.resolveRelated) {
        continue;
      }
      try {
        const contextValue = await snapshot(context.value$);
        const relatedResources =
          await context.resource.resolveRelated(contextValue);
        const newContexts = this._filterNewRelatedContexts(
          relatedResources,
          resources,
          buildContext,
          routeContexts,
          relatedContexts
        );
        relatedContexts.push(...newContexts);
      } catch {
        // Silently ignore resolveRelated failures. These aren't critical.
      }
    }

    return relatedContexts;
  }
}
